import { useCallback, useEffect, useRef, useState } from "react";
import { fitRectInRect, getScreenshot } from "./Session";
import { timestampedBlob, useIntervalRecording } from "./Recorder";
import { Attachment } from "../../Questions/Attachment";
import { SpacedSpinner } from "../../Util";
import { ArrowPathIcon, CameraIcon, CheckCircleIcon } from "@heroicons/react/24/outline";
import { Button, WhiteButton } from "./Buttons";
import { CardFront } from "./CoverImages";

export function LivenessVideoTest(props: any) {
  const loggerHandler: ProxyHandler<string[]> = {
    get: function(target, prop, receiver) {
      if (prop === 'push') {
        return function(element: string) {
          
          if (['development'].includes(process.env.NODE_ENV)) {
            return Reflect.apply(target[prop], target, [`${new Date().toISOString()} ${process.env.NODE_ENV} ${element}`]);
          }
        }
      }
      return Reflect.get(target,prop,receiver);
    }
  }
  const logger = useRef(new Proxy([] as string[], loggerHandler));

  const [videoMode, setVideoMode] = useState(false);

  /* Camera management */
  const videoRef = useRef() as React.MutableRefObject<HTMLVideoElement>;
  const [mediaStream, setMediaStream] = useState(null as MediaStream | null);
  const videoStream = useRef(null as MediaStream | null);

  const [currentImage, setCurrentImage] = useState(null as null | Blob);
  const [currentVideo, setCurrentVideo] = useState(null as null | string);
  const [retrying, setRetrying] = useState(false);
  const [canCloseWindow, setCanCloseWindow] = useState(false);
  
  const [cameraAvailable, setCameraAvailable] = useState(true);
  const [multipleCameras, setMultipleCameras] = useState(false);
  const [devices, setDevices] = useState<Record<string, { id: string, groupId?: string }> | null>(null);
  const [facingMode, setFacingMode] = useState("environment" as "user" | "environment");
  const [videoDimensions, setVideoDimensions] = useState<{
    width: number,
    height: number
    top: number,
    left: number
  } | null>(null)
  const [maskDimensions, setMaskDimensions] = useState<{
    width: number,
    height: number
    top: number,
    left: number
  } | null>(null)

  const [videoPlaying, setVideoPlaying] = useState(false);

  const [idType, setIDType] = useState<"wide" | "tall">("wide");

  const handleResize = useCallback(() => {
    const elWidth = videoRef.current?.offsetWidth;
    const elHeight = videoRef.current?.offsetHeight;

    const width = mediaStream?.getVideoTracks()[0].getSettings().width;
    const height = mediaStream?.getVideoTracks()[0].getSettings().height;

    if (width && height && elWidth && elHeight) {
      const dimensions = fitRectInRect({ width, height }, { width: elWidth, height: elHeight }); 

      const maskDimensions = fitRectInRect(
        { width: 820, height: 560 },
        { width: dimensions.width, height: dimensions.height }
      );
      maskDimensions.top += dimensions.top;
      maskDimensions.left += dimensions.left;

      setVideoDimensions(dimensions);
      setMaskDimensions(maskDimensions);
      setVideoPlaying(true);
    } else {
      setVideoPlaying(false);
    }
  }, [mediaStream, videoRef.current, idType])

  useEffect(() => {
    handleResize();
  }, [idType]);

  /* Layout Management */
  useEffect(() => {
    // Initialization tends to happen in fairly arbitrary orders so we need a bunch of
    // different handlers to ensure we've catch things when they finally settle
    const localHandleResize = () => { handleResize() };

    localHandleResize();
    function handlePlay() { localHandleResize(); }
    function handlePause() { setVideoPlaying(false); }
    videoRef.current?.addEventListener('resize', localHandleResize);
    videoRef.current?.addEventListener('play', handlePlay)
    videoRef.current?.addEventListener('pause', handlePause)
    window.addEventListener('resize', localHandleResize);
    
    return () => {
      videoRef.current?.removeEventListener('resize', localHandleResize);
      videoRef.current?.removeEventListener('play', handlePlay);
      videoRef.current?.removeEventListener('pause', handlePause);
      window.removeEventListener('resize', localHandleResize);
    }
  }, [mediaStream, videoRef.current]);

  /* Camera Management */
  const updateCamera = useCallback(async (facingMode: string) => {
    // Clear the video stream so that we know we arent streaming immediately.
    videoStream.current = null;

    if (mediaStream && videoRef.current) {
      console.log("Swapping cameras");
      videoRef.current.pause();
      videoRef.current.srcObject = null;
      mediaStream.getTracks().forEach((track) => {
        console.log("Stopping", track.label);
        track.stop();
      });
    }

    // If the screen is vertical try to ask for a vertical image
    let width = 1920;
    let height = 1440;
    if (window.innerHeight > window.innerWidth) {
      width = 1440;
      height = 1920;
    }

    const requestedMedia: MediaStreamConstraints = {
      audio: false,
      video: { 
        facingMode: { ideal: facingMode },
        width: { ideal: width },
        height: { ideal: height },
      },
    };

    let stream: MediaStream | undefined;
    try {
      stream = await navigator.mediaDevices.getUserMedia(requestedMedia) as MediaStream;;
      console.log("New stream created:", stream.id, facingMode);
      setMediaStream(stream);
      videoStream.current = stream;
    } catch (e: any) {
      setCameraAvailable(false);
    }
    let devices;
    try {
      devices = await navigator.mediaDevices.enumerateDevices();
      let videoCount = 0;
      const multipleDevices = devices.filter(d => d.kind === 'videoinput').reduce((acc, cur) => {
        acc[cur.label] = {
          id: cur.deviceId,
          groupId: cur.groupId
        }
        videoCount++;
        return acc;
      }, {} as Record<string, any>);
      setDevices(multipleDevices);
      if (videoCount > 1) {
        setMultipleCameras(true);
      }
    } catch (e: any) {
      console.log("No Camera Available 2");
    }
  }, [mediaStream]);

  useEffect(() => {
    updateCamera(facingMode);
  }, [facingMode]);

  const [currentImageUrl, setCurrentImageUrl] = useState(null as null | string);
  useEffect(() => {
    if (!currentImage) return;
    const url = URL.createObjectURL(currentImage);
    setCurrentImageUrl(url);
    return () => {
      URL.revokeObjectURL(url);
    }
  }, [currentImage]);
    
  useEffect(() => {
    if (mediaStream && videoRef.current && !videoRef.current.srcObject) {
      videoRef.current.srcObject = mediaStream;
      videoRef.current.play();
    }
  }, [mediaStream]);

  const swapCamera = useCallback(async () => {
    if (facingMode === "user") {
      setFacingMode("environment");
    } else {
      setFacingMode("user");
    }
    console.log("Swapping camera")
  }, [facingMode]);

  const { recordedChunks, codec, captureReady, stopRecording } = useIntervalRecording(
    mediaStream, 
    logger.current
  );

  const [capturing, setCapturing] = useState(false);

  //
  // captureFromStream grabs an image and video chunks from the current (active)
  // mediaStream, stopping the recording of video in the process.
  //
  const captureFromStream = useCallback(async (): Promise<{ image: Blob; videoChunks: timestampedBlob[][] } | { error: string }> => {
    let image = await getScreenshot(videoRef.current);
    if (!image) return { error: 'Unable to get screenshot' };
    setCurrentImage(image);
    setCapturing(false);

    // Wait for the video to stop
    stopRecording();
    // Wait 1 second
    await new Promise((resolve) => setTimeout(resolve, 1000));

    const currentChunks = recordedChunks.current;

    // Upload the last three recordings, separately
    const lastFew = Object.entries(currentChunks).sort(([a,x],[b,y]) => parseInt(a) - parseInt(b)).slice(-3);
    return { image, videoChunks: lastFew.map(([_, blob]) => (blob))}
  }, [getScreenshot, setCurrentImage, stopRecording])
  
  const debugFrame = false;

  return (<>
    <div className={"bg-gray-50 flex flex-col fixed h-full w-screen "} style={{height: '-webkit-fill-available'}}>
      {/* Top Nav Bar */}
      <div className="flex flex-none">
        <div className="flex-grow text-center self-center text-xs flex justify-center items-center" style={{height: '50px'}}>
          Test Video Capture
        </div>
      </div>

      {/* Main Content */}
      <div className="bg-gray-50 flex flex-grow items-stretch flex-column items-center overflow-scroll" style={{display: (!cameraAvailable) ? 'none' : ''}}>
        <div className="flex h-50 relative">
          {!captureReady && !currentImage && <div style={{
            position: 'absolute',
            height: '100%',
            width: '100%',
            zIndex: 100,
            backgroundColor: 'lightgray'
          }}></div>}
          <video className={`w-full`}
            style={{background: "#f2f2f2", transform: `${(facingMode === 'user' || !multipleCameras) ? 'scaleX(-1)' : ''}`}} 
            ref={videoRef} playsInline muted />
          <div style={{ 
            position: 'absolute', 
            display: 'flex', alignItems: 'center',
            ...videoDimensions
          }} >
            {idType === 'wide' && <CardFront style={{margin: 'auto'}} className="block flex-1 w-full h-full" />}
          </div>
          <div className={debugFrame ? "bg-red-100" : "bg-white"} style={{
            position: 'absolute',
            top: ((maskDimensions?.top || 0) + (maskDimensions?.height || 0)) - 2,
            left: 0,
            width: '100%',
            height: '100%',
          }}></div>
          <div className={debugFrame ? "bg-blue-100" : "bg-white"} style={{
            position: 'absolute',
            top: 0,
            left: ((maskDimensions?.left || 0) + (maskDimensions?.width || 0)) - 2,
            width: '100%',
            height: '100%',
          }}></div>
          <div className={debugFrame ? "bg-yellow-100" : "bg-white"} style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: '100%',
            height: (maskDimensions?.top || 0) + 2,
          }}></div>
          <div className={debugFrame ? "bg-green-100" : "bg-white"} style={{
            position: 'absolute',
            top: 0,
            left: 0,
            width: (maskDimensions?.left || 0) + 2,
            height: '100%',
          }}></div>
        </div>
        <div className="flex h-50 relative">
          { currentImageUrl && <img className="h-full w-full" style={{objectFit: 'contain'}} src={currentImageUrl} />}
        </div>
      </div>

      {/* Loading Spinner for Camera Recording */}
      {
        (!captureReady) && <div className="w-100 flex-none bg-white flex flex-column items-center justify-center h-10">
          <div style={{ zIndex: 100 }} 
            className="mx-auto my-4 justify-center flex items-center px-4 py-2 text-xs font-medium text-gray-700">
            <SpacedSpinner />
            <span>Starting camera</span>
          </div>
        </div>
      }      

      {/* Bottom Nav Bar */}
      <div className="flex flex-none items-center justify-center">
        <div className="flex justify-center flex-1 text-center p-1" style={{ height: '60px' }} >
          <div className="flex-1">{multipleCameras &&
              <WhiteButton onClick={swapCamera}>
                <ArrowPathIcon className="text-gray-800 mr-2" width={30} height={30} />
                Switch cameras
              </WhiteButton>}</div>
          <div className="flex-1">
            {captureReady ? <Button 
              onClick={async () => { 
                const captureResult = await captureFromStream();
                if ('error' in captureResult) {
                  console.log(`CAPTURE ERROR: ${captureResult.error}`);
                }
                else {
                  const { image } = captureResult;
                  setCurrentImage(image);
                }
              }}
              className={`ml-2`}
              disabled={capturing}>
              {capturing ? <SpacedSpinner /> : <CameraIcon className="mr-2" width={30} height={30} />}
              <span>Take photo</span>
            </Button> : null}
          </div>
        </div>
      </div>
    </div>
  </>
  );
}